/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { compact, Dictionary, isEmpty, partition } from 'lodash';
import { css } from 'aphrodite';
import { Text } from '@blend/enterprise-components';

import { BlendBridge } from 'blb-types/blendBridge';
import { closingTypeToDisplay, RONSelectionCopy } from 'blb-constants/global';
import { DocumentSectionComponents } from 'blb-ui-types/BridgeTypes';
import { cardWrapperStyles } from 'blb-components/AuthenticatedApp/DashboardPage/SectionCard';
import { DashboardSection } from 'blb-components/AuthenticatedApp/DashboardPage/DashboardSection';
import { DocumentsCard } from 'blb-components/AuthenticatedApp/DashboardPage/DocumentsCard';
import {
  DocumentTitles,
  EcloseDocumentTypeLabels,
} from 'blb-components/AuthenticatedApp/DashboardPage/DocumentsCard/constants';
import { UploadedDocuments } from 'blb-components/AuthenticatedApp/DashboardPage/docUtils/UploadedDocuments';
import { TaskHeaders } from 'blb-components/AuthenticatedApp/DashboardPage/Task/constants';
import { getConfig } from 'blb-utils/config';
import * as docUtils from '../common/utils/documents';
import { filterAndGroupDocsByDesignations } from '../common/utils/documents';
import * as constants from '../common/constants';
import { RegistrationFormOverrides } from '../';
import {
  getSignAheadDocStatusTag,
  isRONTransactionCompleted,
  renderENoteDocumentCard,
  renderPreClosingDocuments,
  renderReceivedRONClosingDocuments,
  renderReceivedRONClosingDocumentsOldVersion,
  renderSettlementOnlyDocumentCard,
} from '../common/utils/renderDocuments';
import {
  getSupportContactInfoForPrincipal,
  renderDefaultDashboardOverview,
  renderDefaultProfileSidebar,
  SupportContact,
} from '../common';
import { MeContext } from 'blb-contexts/Me';
import { TabType } from 'blb-components/AuthenticatedApp/ProfilePage';
import { Sidebar } from 'blb-components/Sidebar';
import { UserIconButton } from 'blb-components/AuthenticatedApp/UserIconButton';
import TeamsIconButton from 'blb-components/TeamsIconButton';
import { Route } from 'blb-components/AuthenticatedApp/routeConfiguration';
import { Pipeline } from '@blend/search-client';
import { getFiltersForSettlementAgent } from 'blb-utils/pipeline';
import { SettlementAgentFilter } from 'blb-components/AuthenticatedApp/PipelinePage/NewPipeline/SettlementAgentFilter';
import { FilterParams, SettlementAgentFilterParams } from 'blb-constants/pipelineFilter';
import { DocWarnings } from 'blb-components/DocWarnings';
import * as personaDocumentUtils from 'blb-persona/common/utils/documents';
import { HelpAction } from 'blb-components/AuthenticatedApp/HelpAction';
import { DashboardSectionItem } from 'blb-components/AuthenticatedApp/DashboardPage/DashboardSectionItem';

export {
  shouldDownloadModalInterrupt,
  shouldShowUnsignedESignUIs,
  getESignBannerConfig,
  getDashboardBanner,
} from '../common';

export const getSupportContactInfo = (): SupportContact => getSupportContactInfoForPrincipal('settlement_agent');

const getRonDocumentSectionComponents = (
  receivedDocs: BlendBridge.API.Document[],
  docIdToRefMap: Dictionary<EClose.API.DocumentReferenceResponse>,
  uploadedDocs: BlendBridge.API.Document[],
  preClosingDocuments: BlendBridge.API.Document[],
  loan: BlendBridge.API.Loan,
  auditEvents: EClose.AuditEventResponse[] | undefined,
  removeDoc: (id: string) => void,
  me: BlendBridge.API.User,
): DocumentSectionComponents => {
  // On completion of RON session where all documents are signed, closingStatus is changed to COMPLETED
  // Thus, we assume signatures are completed.
  const uploadedAndDayOfDocumentsTag = isRONTransactionCompleted(loan)
    ? constants.SIGNATURE_COMPLETED_TAG
    : constants.PENDING_SIGNATURE_TAG;

  const { useNewRONDocumentCards } = getConfig();
  const enableSettlementAgentOnlyDocumentCard = loan.featureFlags?.enableSettlementAgentOnlyDocumentCard;

  const [settlementOnlyDocs, nonSettlementOnlyDocs] = partition(
    receivedDocs,
    doc => doc.type === 'CLOSING_SETTLEMENT_ONLY',
  );

  const effectiveDocs = enableSettlementAgentOnlyDocumentCard ? nonSettlementOnlyDocs : receivedDocs;

  const ronClosingDocumentsCard = (useNewRONDocumentCards
    ? renderReceivedRONClosingDocuments
    : renderReceivedRONClosingDocumentsOldVersion)(effectiveDocs, docIdToRefMap, loan, auditEvents);
  const eNoteDocs = personaDocumentUtils.filterDocsByDesignations(effectiveDocs, docIdToRefMap, ['NOTE']);

  return {
    receivedDocComponent: (
      <div>
        <DocWarnings loan={loan} />
        <div className={css(cardWrapperStyles.cardGroup)}>
          {ronClosingDocumentsCard}
          {renderPreClosingDocuments(preClosingDocuments, auditEvents)}
          {renderENoteDocumentCard(loan.closing?.eNoteData, eNoteDocs)}
          {enableSettlementAgentOnlyDocumentCard &&
            !isEmpty(settlementOnlyDocs) &&
            renderSettlementOnlyDocumentCard(settlementOnlyDocs, auditEvents)}
        </div>
      </div>
    ),
    uploadedDocComponent: (
      <UploadedDocuments
        documents={uploadedDocs}
        headerChildren={uploadedAndDayOfDocumentsTag}
        removeDoc={removeDoc}
        uploadedById={me.principalId}
      />
    ),
  };
};

const getHybridDocumentSectionComponents = (
  receivedDocs: BlendBridge.API.Document[],
  docIdToRefMap: Dictionary<EClose.API.DocumentReferenceResponse>,
  uploadedDocs: BlendBridge.API.Document[],
  preClosingDocuments: BlendBridge.API.Document[],
  loan: BlendBridge.API.Loan,
  auditEvents: EClose.AuditEventResponse[] | undefined,
  removeDoc: (id: string) => void,
  me: BlendBridge.API.User,
): DocumentSectionComponents => {
  const enableSettlementAgentOnlyDocumentCard = loan.featureFlags?.enableSettlementAgentOnlyDocumentCard;
  const [settlementOnlyDocs, nonSettlementOnlyDocs] = partition(
    receivedDocs,
    doc => doc.type === 'CLOSING_SETTLEMENT_ONLY',
  );
  const designations: EClose.DocumentClosingDesignation[] = ['DAY_OF', 'SIGN_AHEAD', 'NOTE'];
  const effectiveDocs = enableSettlementAgentOnlyDocumentCard ? nonSettlementOnlyDocs : receivedDocs;
  const {
    DAY_OF: dayOfDocs = [],
    SIGN_AHEAD: signAheadDocs = [],
    NOTE: eNoteDocs = [],
  } = filterAndGroupDocsByDesignations(effectiveDocs, docIdToRefMap, designations);

  // If enableSettlementAgentOnlyDocumentCard is off, we need to find CLOSING_SETTLEMENT_ONLY
  // docs with SIGN_AHEAD designation and add them to DAY_OF docs
  const updatedDayOfDocs = [...dayOfDocs];
  const updatedSignAheadDocs = [...signAheadDocs];

  if (!enableSettlementAgentOnlyDocumentCard) {
    const settlementOnlySignAheadDocs = personaDocumentUtils.filterDocsByDesignations(
      settlementOnlyDocs,
      docIdToRefMap,
      ['SIGN_AHEAD'],
    );

    if (settlementOnlySignAheadDocs.length > 0) {
      updatedDayOfDocs.push(...settlementOnlySignAheadDocs);
      // Remove these documents from SIGN_AHEAD if they somehow ended up there
      const settlementOnlySignAheadDocIds = new Set(
        settlementOnlySignAheadDocs.map((doc: BlendBridge.API.Document) => doc._id),
      );
      const filteredSignAheadDocs = signAheadDocs.filter(
        (doc: BlendBridge.API.Document) => !settlementOnlySignAheadDocIds.has(doc._id),
      );
      updatedSignAheadDocs.splice(0, updatedSignAheadDocs.length, ...filteredSignAheadDocs);
    }
  }

  const signAheadDocsTag: JSX.Element | undefined = getSignAheadDocStatusTag(loan);

  return {
    receivedDocComponent: (
      <div>
        <DocWarnings loan={loan} />
        <div className={css(cardWrapperStyles.cardGroup)}>
          {!isEmpty(updatedDayOfDocs) && (
            <div className={css(cardWrapperStyles.singleCardWrapper)}>
              <DocumentsCard
                auditEvents={auditEvents}
                documentTypeLabel={EcloseDocumentTypeLabels.INK_SIGN_CLOSING_PACKAGE}
                documents={updatedDayOfDocs}
                headerChildren={constants.PRINT_FOR_CLOSING_DAY_TAG}
                id="it-LenderInkSign"
                isUploaded={false}
                key="it-LenderInkSign"
                title={DocumentTitles.LENDER_INK_SIGN_CLOSING_DOCUMENTS}
              />
            </div>
          )}
          {!isEmpty(updatedSignAheadDocs) && (
            <div className={css(cardWrapperStyles.singleCardWrapper)}>
              <DocumentsCard
                auditEvents={auditEvents}
                documentTypeLabel={EcloseDocumentTypeLabels.ESIGN_CLOSING_PACKAGE}
                documents={updatedSignAheadDocs}
                headerChildren={signAheadDocsTag}
                id="it-LenderElectronicClosingDocuments"
                isUploaded={false}
                key="it-LenderElectronicClosingDocuments"
                title={DocumentTitles.LENDER_ESIGN_CLOSING_DOCUMENTS}
              />
            </div>
          )}
          {renderPreClosingDocuments(preClosingDocuments, auditEvents)}
          {renderENoteDocumentCard(loan.closing?.eNoteData, eNoteDocs)}
          {enableSettlementAgentOnlyDocumentCard &&
            !isEmpty(settlementOnlyDocs) &&
            renderSettlementOnlyDocumentCard(settlementOnlyDocs, auditEvents)}
        </div>
      </div>
    ),

    uploadedDocComponent: (
      <UploadedDocuments documents={uploadedDocs} removeDoc={removeDoc} uploadedById={me.principalId} />
    ),
  };
};

const auditEventsByDescendingCreatedAt = (auditEvents: EClose.AuditEventResponse[]): EClose.AuditEventResponse[] =>
  auditEvents.sort((a, b) => new Date(b.createdAt).valueOf() - new Date(a.createdAt).valueOf());

export const getTraditionalAndUndeterminedClosingDocumentSectionComponents = (
  receivedDocs: BlendBridge.API.Document[],
  uploadedDocs: BlendBridge.API.Document[],
  preClosingDocuments: BlendBridge.API.Document[],
  loan: BlendBridge.API.Loan,
  auditEvents: EClose.AuditEventResponse[] | undefined,
  removeDoc: (id: string) => void,
  me: BlendBridge.API.User,
): DocumentSectionComponents => {
  const enableSettlementAgentOnlyDocumentCard = loan.featureFlags?.enableSettlementAgentOnlyDocumentCard;
  const [settlementOnlyDocs, nonSettlementOnlyDocs] = partition(
    receivedDocs,
    doc => doc.type === 'CLOSING_SETTLEMENT_ONLY',
  );
  const wetSignDocs = enableSettlementAgentOnlyDocumentCard ? nonSettlementOnlyDocs : receivedDocs;

  return {
    receivedDocComponent: (
      <div className={css(cardWrapperStyles.cardGroup)}>
        {!isEmpty(wetSignDocs) && (
          <div className={css(cardWrapperStyles.singleCardWrapper)}>
            <DocumentsCard
              auditEvents={auditEvents}
              documentTypeLabel={EcloseDocumentTypeLabels.INK_SIGN_CLOSING_PACKAGE}
              documents={wetSignDocs}
              headerChildren={constants.PRINT_FOR_CLOSING_DAY_TAG}
              id="it-LenderInkSign"
              isUploaded={false}
              key="it-LenderInkSign"
              title={DocumentTitles.LENDER_INK_SIGN_CLOSING_DOCUMENTS}
            />
          </div>
        )}
        {renderPreClosingDocuments(preClosingDocuments, auditEvents)}
        {enableSettlementAgentOnlyDocumentCard &&
          !isEmpty(settlementOnlyDocs) &&
          renderSettlementOnlyDocumentCard(settlementOnlyDocs, auditEvents)}
      </div>
    ),
    uploadedDocComponent: (
      <UploadedDocuments documents={uploadedDocs} removeDoc={removeDoc} uploadedById={me.principalId} />
    ),
  };
};

// exported for tests only
export const getDocumentSectionConfigs = (
  me: BlendBridge.API.User,
  loan: BlendBridge.API.Loan,
  loanDocuments: BlendBridge.API.Document[],
  removeDoc: (id: string) => void,
): DashboardSection.Props['sectionConfigs'] => {
  const closingType = loan.closing?.ronSelectionData?.derivedClosingType || loan.closing?.closingType;
  const auditEvents = loan.closing?.auditEvents;
  const allClosingDocumentReferences = docUtils.getAllClosingDocumentReferences(
    loan.closing?.documentReferences,
    auditEvents,
  );
  const docIdToRefMap = docUtils.convertDocumentReferencesToDocIdToRefMap(allClosingDocumentReferences);
  // uploadedDocs: documents that were uploaded by any settlement agent
  const [uploadedDocs, allReceivedDocs] = partition(loanDocuments, doc => doc.uploadedBySA === true);
  // receivedDocs: documents that were not uploaded by a settlement agent, excluding any Preliminary CD.
  const receivedDocs = docUtils.getReceivedClosingDocumentsFromAllReceivedDocs(allReceivedDocs);
  // preClosingDocuments: preliminary CD with closing designation PRE_CLOSING.
  const preClosingDocuments = docUtils.filterDocsByTypes(
    docUtils.filterDocsByDesignations(allReceivedDocs, docIdToRefMap, ['PRE_CLOSING']),
    [constants.ECloseDocumentTypes.PRELIMINARY_CLOSING_DOCUMENTS],
  );
  const { receivedDocComponent, uploadedDocComponent } = (() => {
    switch (closingType) {
      case 'HYBRID': {
        return getHybridDocumentSectionComponents(
          receivedDocs,
          docIdToRefMap,
          uploadedDocs,
          preClosingDocuments,
          loan,
          auditEvents,
          removeDoc,
          me,
        );
      }
      case 'RON': {
        return getRonDocumentSectionComponents(
          receivedDocs,
          docIdToRefMap,
          uploadedDocs,
          preClosingDocuments,
          loan,
          auditEvents,
          removeDoc,
          me,
        );
      }
      case 'TRADITIONAL':
      default: {
        return getTraditionalAndUndeterminedClosingDocumentSectionComponents(
          receivedDocs,
          uploadedDocs,
          preClosingDocuments,
          loan,
          auditEvents,
          removeDoc,
          me,
        );
      }
    }
  })();

  return compact([
    {
      content: isEmpty(allReceivedDocs) ? <Text>No documents yet.</Text> : receivedDocComponent,
      title: 'Received Documents',
    },
    {
      content: isEmpty(uploadedDocs) ? <Text>Nothing uploaded yet.</Text> : uploadedDocComponent,
      title: 'Uploaded Documents',
    },
  ]);
};

/**
 * Determines if the current task is the proper task the interrupt modal
 * should be associated with.
 * @param  {string} taskHeader The label of the Task
 * @return {boolean}           Boolean flag
 */
export const shouldUploadModalInterrupt = (taskHeader: string): boolean =>
  taskHeader === TaskHeaders.UPLOAD_CLOSING_PACKAGE;

export const getRegisrationFormOverrides = (): RegistrationFormOverrides => {
  const supportEmail = getConfig().supportEmail.settlement_agent!;
  return {
    headerLabelOverride: (
      <Text>
        We are introducing profiles for settlement agents. As part of this update, we are asking you to provide us with
        some basic information about your title agency. This will help us to create easier ways for you to work with
        your team and simplify certain processes such as billing. Please reach out to{' '}
        <a href={`mailto:${supportEmail}`}>{supportEmail}</a> with any questions.
      </Text>
    ),
    lineTextOverride: {
      label: (
        <Text>
          Address (please enter your <Text weight="bold">BUSINESS ADDRESS</Text>)
        </Text>
      ),
    },
  };
};

export const renderDashboardOverview = (
  dashboardSections: DashboardSectionItem.SectionConfig[],
  loan: BlendBridge.API.Loan,
  documents: BlendBridge.API.Document[],
  removeDoc: (id: string) => void,
  setInstructionOpen: (val: boolean) => void,
  areInstructionsOpen: boolean,
): React.ReactElement =>
  renderDefaultDashboardOverview(
    dashboardSections,
    loan,
    getDocumentSectionConfigs(React.useContext(MeContext)!, loan, documents, removeDoc),
    setInstructionOpen,
    areInstructionsOpen,
  );

export const getClosingTypeCopy = (closing: EClose.FullClosing): string => {
  const closingTypeDisplayed = closingTypeToDisplay[closing.closingType];
  if (closing.closingType === 'RON') {
    // TODO: BRG-2089: do not use AuditEvent. Instead add a ronPlatform field to fullClosing.
    const mostRecentRONProviderAuditEvent = auditEventsByDescendingCreatedAt(closing.auditEvents).find(
      event => event.type === 'RON_PROVIDER_SELECTED',
    );
    if (mostRecentRONProviderAuditEvent !== undefined) {
      const ronPlatform = mostRecentRONProviderAuditEvent.meta!.ronPlatform as EClose.RonPlatformTypes;
      return `${closingTypeDisplayed} ${RONSelectionCopy[ronPlatform]}`;
    }
  }
  return closingTypeDisplayed;
};

export const getProfileTabs = (_me: BlendBridge.API.User): TabType[] => {
  const { isBranchTabEnabled, isProfileRequiredForSA } = getConfig();
  const tabs: TabType[] = [];

  if (isProfileRequiredForSA) tabs.push('Profile');
  if (isBranchTabEnabled) tabs.push('Branches');

  return tabs;
};

// TODO: Remove this function once all persona's have a `getSidebar` function defined
export const renderProfileSidebar = renderDefaultProfileSidebar;

export const getSidebar = (): React.ReactElement => {
  // TODO: replace EC buttons with Alchemy buttons in sidebar all at once
  const lowButtons = [<UserIconButton key="sa-profile-icon" />];

  if (getConfig().isTeamsUIEnabled) {
    lowButtons.push(<TeamsIconButton key="sa-teams-icon" />);
  }
  lowButtons.push(<HelpAction key="sa-help-icon" />);

  return <Sidebar highButtons={[]} lowButtons={lowButtons} rootRoutes={['/pipeline']} />;
};

export const showSpecialInstructions = (): boolean => true;

export const getAllowedRoutes = (me: BlendBridge.API.User): { allowedRoutes: Route[]; defaultLandingPath: Route } => {
  if (getConfig().isProfileRequiredForSA && !me.isProfileComplete) {
    return {
      allowedRoutes: ['/registration'],
      defaultLandingPath: '/registration',
    };
  }

  const allowedRoutes: Route[] = ['/pipeline', '/:loanId/dashboard', '/:loanId/sign-ahead-documents', '/:loanId/enote'];
  if (getConfig().isProfileRequiredForSA) {
    allowedRoutes.push('/profile');
  }
  if (getConfig().isTeamsUIEnabled) {
    allowedRoutes.push('/teams');
  }

  return {
    allowedRoutes,
    defaultLandingPath: '/pipeline',
  };
};

export const renderProfileHeader = () => undefined;

export const getPipelineFilters = (filterParams: FilterParams): Pipeline.IFilter[] => {
  return getFiltersForSettlementAgent(filterParams as SettlementAgentFilterParams);
};

export const renderPipelineFilter = (
  filters: SettlementAgentFilterParams,
  onFiltersChanged: (filterParams: FilterParams) => void,
) => {
  return <SettlementAgentFilter filters={filters} onFilterChanged={onFiltersChanged} />;
};

export const getPipelinePollingInterval = (): number => 1000 * 10;
